import React, { useState } from "react";
import { FaFlag, FaTimes } from "react-icons/fa";
import api from "../../utils/api/axiosInstance";
import { useToast } from "../../contexts/ToastContext";
import styles from "../../styles/components/moderation/ReportModal.module.css";

const REPORT_REASONS = [
  { value: "spam", label: "Spam or misleading" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "misinformation", label: "False information" },
  { value: "other", label: "Other (please describe)" },
];

const isTerminalReportError = (message) => {
  const normalized = String(message).toLowerCase();
  return (
    normalized.includes("cannot report your own") ||
    normalized.includes("already reported")
  );
};

const ReportModal = ({ isOpen, onClose, contentType, contentId, onReported }) => {
  const { showToast } = useToast();
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const closeModal = () => {
    setReason("");
    setDescription("");
    onReported?.();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason) {
      showToast({ message: "Please select a reason", type: "error" });
      return;
    }

    if (reason === "other" && !description.trim()) {
      showToast({ message: "Please provide a description", type: "error" });
      return;
    }

    setLoading(true);

    try {
      const endpoint =
        contentType === "post"
          ? `/reports/posts/${contentId}`
          : contentType === "comment"
          ? `/reports/comments/${contentId}`
          : `/reports/questions/${contentId}`;

      await api.post(endpoint, {
        reason,
        description: description.trim(),
      });

      showToast({ message: "Report submitted successfully", type: "success" });
      closeModal();
    } catch (err) {
      const message = err.response?.data?.message || "Failed to submit report";
      showToast({ message, type: "error" });
      if (isTerminalReportError(message)) {
        closeModal();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason("");
      setDescription("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.title}>
            <FaFlag /> Report {contentType === "post" ? "Post" : contentType === "comment" ? "Comment" : "Question"}
          </div>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            disabled={loading}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="reason" className={styles.label}>
              Why are you reporting this {contentType === "question" ? "question" : contentType}?
            </label>
            <select
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={styles.select}
              disabled={loading}
              required
            >
              <option value="">Select a reason...</option>
              {REPORT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {(reason === "other" || description) && (
            <div className={styles.formGroup}>
              <label htmlFor="description" className={styles.label}>
                Additional details {reason === "other" && "(required)"}
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={styles.textarea}
                placeholder="Please provide more context..."
                rows={4}
                maxLength={500}
                disabled={loading}
                required={reason === "other"}
              />
              <div className={styles.charCount}>
                {description.length}/500 characters
              </div>
            </div>
          )}

          <div className={styles.info}>
            <p>
              Your report will be reviewed by our moderation team. False reports
              may result in action being taken on your account.
            </p>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              onClick={handleClose}
              className="buttonSecondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="buttonPrimary"
              disabled={loading || !reason}
            >
              {loading ? "Submitting..." : "Submit Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportModal;
